import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { APIError } from '@/queries/types';

/**
 * Radix's Dialog scroll-lock only recognises scrollable regions that are DOM
 * descendants of the dialog's own content. Popovers, dropdowns, and other
 * portaled components render to `document.body` as siblings instead, so the
 * lock's wheel/touchmove listener (attached on `document`, capture phase)
 * swallows scroll events over them — nested scrolling inside a Popover
 * rendered within a Dialog silently stops working.
 *
 * This intercepts those events one level higher, on `window` — which fires
 * first in the capture phase — and stops them from ever reaching the lock's
 * listener, so the browser's native scroll proceeds inside the portaled
 * content. `<body>` itself stays non-scrollable regardless (the lock also
 * sets `overflow: hidden` via CSS while active), so the page behind the
 * dialog doesn't start scrolling as a side effect.
 *
 * Courtesy of https://github.com/radix-ui/primitives/issues/3276#issuecomment-3567446826.
 */
export const usePreventScrollLock = () => {
    useLayoutEffect(() => {
        const handler = (e: Event) => {
            if (document.body.getAttribute('data-scroll-locked') === '1') {
                e.stopImmediatePropagation();
            }
        };

        const eventTypes = ['wheel', 'scroll', 'touchmove'] as const;

        const options = { capture: true, passive: false };
        eventTypes.forEach((type) => {
            window.addEventListener(type, handler, options);
        });

        return () => {
            eventTypes.forEach((type) => {
                window.removeEventListener(type, handler, options);
            });
        };
    }, []);
};

export const useTopRef = () => {
    const topRef = useRef<HTMLDivElement>(null);
    const TopRef = () => <div ref={topRef} />;
    return { topRef, TopRef };
};

/**
 * `'background'`: a query 401'd — low-stakes, nothing the user was doing failed.
 * `'action'`: a mutation 401'd — the user's last action (e.g. a save) didn't go
 * through, which needs to be communicated more specifically/urgently. Escalates
 * only, never downgrades — a mutation failing after a query already failed should
 * upgrade the notification, not get lost behind it.
 */
export type SessionExpiredSeverity = 'none' | 'background' | 'action';

/**
 * Builds a `QueryClient` scoped to one authenticated subtree (system or client),
 * whose queries/mutations escalate `severity` the moment any of them see a 401 —
 * there's no refresh token, so a 401 anywhere means the session died and every
 * other in-flight/future call in this subtree will fail the same way. Each caller
 * gets its own independent client/cache/state; only this construction logic is
 * shared.
 */
export function useAuthenticatedQueryClient() {
    const [severity, setSeverity] = useState<SessionExpiredSeverity>('none');

    function handleQueryAuthError(error: unknown) {
        if (error instanceof APIError && error.statusCode === 401) {
            setSeverity((current) => (current === 'none' ? 'background' : current));
        }
    }

    function handleMutationAuthError(error: unknown) {
        if (error instanceof APIError && error.statusCode === 401) setSeverity('action');
    }

    // A 401 means the session is dead — retrying just delays the notification for no
    // benefit. Mutations aren't included: React Query already defaults them to 0
    // retries (they can have side effects), so there's nothing to shortcut there.
    function retryQuery(failureCount: number, error: unknown) {
        if (error instanceof APIError && error.statusCode === 401) return false;
        return failureCount < 3; // matches React Query's own default
    }

    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: { retry: retryQuery },
                },
                queryCache: new QueryCache({ onError: handleQueryAuthError }),
                mutationCache: new MutationCache({ onError: handleMutationAuthError }),
            })
    );

    return { queryClient, severity };
}
